import React, { useCallback, useEffect, useState } from 'react';
import { CommissionScheduleService } from '@vanilla/services/CommissionScheduleService.js';
import { formatCurrency } from '@vanilla/utils/Utils.js';
import { Badge, statusBadgeVariant } from '@/components/ui/badge';

/**
 * Persisted commission accruals by month (SoT DealFormManager.renderCommissionSchedule).
 */
export default function DealCommissionSchedulePanel({ dealId, commissionEligible, users = [] }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        if (!dealId) {
            setRows([]);
            return;
        }
        setLoading(true);
        try {
            const list = await CommissionScheduleService.getByDeal(dealId);
            setRows(Array.isArray(list) ? list : []);
        } catch (err) {
            console.warn('[DealCommissionSchedulePanel] load failed', err);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [dealId]);

    useEffect(() => {
        reload();
        const onRefresh = () => reload();
        window.addEventListener('dealCommissionScheduleRefresh', onRefresh);
        return () => window.removeEventListener('dealCommissionScheduleRefresh', onRefresh);
    }, [reload]);

    const ineligibleHtml = commissionEligible === false;

    if (!dealId) {
        return (
            <div id="dealCommissionScheduleHost" className="deal-schedule-host" style={{ marginTop: 16 }}>
                <p className="field-hint">Save the deal to view commission schedule.</p>
            </div>
        );
    }

    const byMonth = {};
    rows.forEach((r) => {
        const key = (r.invoiceDate || '').slice(0, 7) || 'Unknown';
        if (!byMonth[key]) byMonth[key] = [];
        byMonth[key].push(r);
    });
    const months = Object.keys(byMonth).sort();
    const userMap = Object.fromEntries(users.map((u) => [u.id, u.name || u.email || u.id]));

    return (
        <div id="dealCommissionScheduleHost" className="deal-schedule-host" style={{ marginTop: 16 }}>
            {ineligibleHtml && (
                <div className="commission-eligibility-notice">
                    <strong>Commission ineligible</strong>
                    <span> Existing accruals remain visible. New accruals are blocked while this deal is ineligible.</span>
                </div>
            )}
            {loading && <p className="field-hint">Loading commission accruals…</p>}
            {!loading && !rows.length && <p className="field-hint">No commission accruals yet.</p>}
            {!loading && months.map((month) => (
                <div className="commission-timeline-month" key={month}>
                    <h4>{month}</h4>
                    <ul className="commission-timeline-list">
                        {byMonth[month].map((r) => (
                            <li key={r.id || `${r.invoiceCode}-${r.ownerUserId}-${r.amount}`}>
                                <Badge variant={statusBadgeVariant(r.status)} className={`status-pill status-${r.status}`}>{r.status}</Badge>
                                {' '}{r.invoiceCode} · {userMap[r.ownerUserId] || 'Rep'} · {formatCurrency(r.amount)}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}
